import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CompanyContextService } from '../../../../core/services/company-context.service';
import { GlobalAlertService } from '../../../../core/services/global-alert.service';
import { SessionService } from '../../../../core/services/session.service';
import {
  CreateUserRequest,
  UpdateUserRequest,
  User,
} from '../../data/models/user.models';
import { UsersService } from '../../data/services/users.service';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './users.html',
  styleUrl: './users.scss',
})
export class Users {
  private readonly usersService = inject(UsersService);
  private readonly sessionService = inject(SessionService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly globalAlertService = inject(GlobalAlertService);
  private readonly fb = inject(FormBuilder);

  readonly selectedCompanyId = this.companyContextService.selectedCompanyId;
  readonly isSuperAdmin = this.sessionService.isSuperAdmin;

  searchTerm = '';
  private searchSubject = new Subject<string>();

  users: User[] = [];
  totalCount = 0;
  pageNumber = 1;
  pageSize = 20;
  totalPages = 0;

  isLoading = false;
  errorMessage = '';

  isCreateModalOpen = false;
  isSaving = false;
  isEditMode = false;
  editingUserId: number | null = null;
  deletingUserId: number | null = null;
  resettingUserId: number | null = null;

  isDeleteModalOpen = signal(false);
  deletingUser: User | null = null;

  isResetModalOpen = signal(false);
  resettingUser: User | null = null;

  // Assuming roles dictionary while there's no dynamic Roles API endpoint provided
  readonly allRoleOptions = [
    { id: 1, name: 'Super Admin' },
    { id: 2, name: 'Company Admin' },
    { id: 3, name: 'User' },
  ];

  readonly roleOptions = computed(() => {
    if (this.sessionService.isSuperAdmin()) {
      return this.allRoleOptions;
    }
    // Filter out Super Admin (id: 1) for non-Super Admins
    return this.allRoleOptions.filter(role => role.id !== 1);
  });

  readonly statusOptions = ['Active', 'Inactive'];

  readonly userForm = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    phoneNumber: ['', [Validators.required]],
    username: ['', [Validators.required]], // New field
    roleId: [3, [Validators.required]],
    status: ['Active', [Validators.required]],
  });

  constructor() {
    effect(() => {
      const companyId = this.selectedCompanyId();
      // Reset to page 1 on company change
      this.pageNumber = 1;
      this.loadUsers(companyId);
    });

    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => {
        this.searchTerm = term;
        this.pageNumber = 1; // reset to first page on search
        this.loadUsers(this.selectedCompanyId());
      });
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  loadUsers(companyId: number): void {
    if (companyId === undefined || companyId === null) return;

    this.isLoading = true;
    this.errorMessage = '';

    this.usersService
      .getUsers(companyId, this.pageNumber, this.pageSize, this.searchTerm)
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.users = response.data.users;
            this.totalCount = response.data.totalCount;
            this.totalPages = response.data.totalPages || Math.ceil(this.totalCount / this.pageSize);
          } else {
            this.errorMessage = response.message || 'Failed to load users.';
            this.users = [];
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.errorMessage = err?.error?.message || 'Failed to load users.';
          this.users = [];
          this.isLoading = false;
        },
      });
  }

  // --- Pagination Methods ---
  nextPage(): void {
    if (this.pageNumber < this.totalPages) {
      this.pageNumber++;
      this.loadUsers(this.selectedCompanyId());
    }
  }

  previousPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.loadUsers(this.selectedCompanyId());
    }
  }

  // --- User Form Actions ---
  openCreateUserModal(): void {
    this.isEditMode = false;
    this.editingUserId = null;

    this.userForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      username: '',
      roleId: 3, // Default to user
      status: 'Active',
    });

    this.errorMessage = '';
    this.isCreateModalOpen = true;
  }

  openEditUserModal(user: User): void {
    this.isEditMode = true;
    this.editingUserId = user.userId;

    this.userForm.reset({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      username: user.username,
      roleId: user.roleId || 3,
      status: user.isActive ? 'Active' : 'Inactive',
    });

    this.errorMessage = '';
    this.isCreateModalOpen = true;
  }

  closeCreateUserModal(): void {
    this.isCreateModalOpen = false;
    this.isEditMode = false;
    this.editingUserId = null;
    this.userForm.markAsPristine();
    this.userForm.markAsUntouched();
  }

  onCreateUser(): void {
    this.openCreateUserModal();
  }

  saveUser(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const formValue = this.userForm.getRawValue();
    const roleId = formValue.roleId ?? 3;

    // Security check: Only Super Admin can create/update Super Admin role
    if (roleId === 1 && !this.sessionService.isSuperAdmin()) {
      this.errorMessage = 'Only Super Admin can create or update a user with Super Admin role.';
      return;
    }

    // Additional check: Only Super Admin can update someone who is already a Super Admin
    if (this.isEditMode && this.editingUserId !== null) {
      const userBeingEdited = this.users.find((u) => u.userId === this.editingUserId);
      if (userBeingEdited?.roleId === 1 && !this.sessionService.isSuperAdmin()) {
        this.errorMessage = 'Only Super Admin can update a Super Admin.';
        return;
      }
    }

    this.isSaving = true;
    this.errorMessage = '';

    const isActive = (formValue.status ?? 'Active') === 'Active';

    if (this.isEditMode && this.editingUserId !== null) {
      const payload: UpdateUserRequest = {
        roleId: formValue.roleId ?? 3,
        firstName: formValue.firstName ?? '',
        lastName: formValue.lastName ?? '',
        phoneNumber: formValue.phoneNumber ?? '',
        email: formValue.email ?? '',
        username: formValue.username ?? '',
        isActive: isActive,
      };

      this.usersService.updateUser(this.editingUserId, payload).subscribe({
        next: (response) => {
          if (response.success) {
            // refresh page
            this.loadUsers(this.selectedCompanyId());
            this.closeCreateUserModal();
          } else {
            this.errorMessage = response.message || 'Failed to update user.';
          }
          this.isSaving = false;
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMessage = err?.error?.message || 'Failed to update user.';
        },
      });

      return;
    }

    const reqRoleId = formValue.roleId ?? 3;

    const payload: CreateUserRequest = {
      companyId: reqRoleId === 1 ? 0 : this.selectedCompanyId(),
      roleId: reqRoleId,
      firstName: formValue.firstName ?? '',
      lastName: formValue.lastName ?? '',
      phoneNumber: formValue.phoneNumber ?? '',
      email: formValue.email ?? '',
      username: formValue.username ?? '',
      isActive: isActive,
    };

    this.usersService.createUser(payload).subscribe({
      next: (response) => {
        if (response.success) {
          // reload list to get the new user with correct pagination
          this.pageNumber = 1;
          this.loadUsers(this.selectedCompanyId());
          this.closeCreateUserModal();
        } else {
          this.errorMessage = response.message || 'Failed to create user.';
        }
        this.isSaving = false;
      },
      error: (err) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to create user.';
      },
    });
  }

  // --- Deletion Actions ---
  openDeleteModal(user: User): void {
    this.deletingUser = user;
    this.isDeleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen.set(false);
    this.deletingUser = null;
  }

  confirmDeleteUser(): void {
    if (!this.deletingUser) return;

    const userId = this.deletingUser.userId;

    // Security check: Only Super Admin can delete Super Admin
    if (this.deletingUser.roleId === 1 && !this.sessionService.isSuperAdmin()) {
      this.errorMessage = 'Only Super Admin can delete a Super Admin.';
      this.closeDeleteModal();
      return;
    }

    this.deletingUserId = userId;

    this.usersService.deleteUser(userId).subscribe({
      next: (response) => {
        if (response.success) {
          // Optionally adjust pageNumber if we deleted the last item on the page
          if (this.users.length === 1 && this.pageNumber > 1) {
            this.pageNumber--;
          }
          this.loadUsers(this.selectedCompanyId());
          this.closeDeleteModal();
        } else {
          this.errorMessage = response.message || 'Failed to delete user.';
          this.closeDeleteModal();
        }
        this.deletingUserId = null;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'Failed to delete user.';
        this.deletingUserId = null;
        this.closeDeleteModal();
      },
    });
  }

  onDeleteUser(user: User): void {
    this.openDeleteModal(user);
  }

  // --- Reset Password Action ---
  onResetPassword(user: User): void {
    this.resettingUser = user;
    this.isResetModalOpen.set(true);
  }

  closeResetModal(): void {
    this.isResetModalOpen.set(false);
    this.resettingUser = null;
  }

  confirmResetPassword(): void {
    if (!this.resettingUser) return;

    const user = this.resettingUser;

    // Security check: Only Super Admin can reset Super Admin password
    if (user.roleId === 1 && !this.sessionService.isSuperAdmin()) {
      this.globalAlertService.showError('Only Super Admin can reset a Super Admin password.');
      this.closeResetModal();
      return;
    }

    this.resettingUserId = user.userId;
    this.usersService.resetUserPassword(user.userId).subscribe({
      next: (response) => {
        this.resettingUserId = null;
        this.closeResetModal();
        if (response.success) {
          this.globalAlertService.showSuccess(`Password reset successfully for ${user.firstName}.`);
        } else {
          this.globalAlertService.showError(response.message || 'Failed to reset password.');
        }
      },
      error: (err) => {
        this.resettingUserId = null;
        this.closeResetModal();
        this.globalAlertService.showError(err?.error?.message || 'Failed to reset password.');
      }
    });
  }

  // --- Form Controls ---
  get firstNameControl() {
    return this.userForm.get('firstName');
  }

  get lastNameControl() {
    return this.userForm.get('lastName');
  }

  get emailControl() {
    return this.userForm.get('email');
  }

  get phoneNumberControl() {
    return this.userForm.get('phoneNumber');
  }

  get usernameControl() {
    return this.userForm.get('username');
  }

  // --- Utility ---
  clearError(): void {
    this.errorMessage = '';
  }
}